/**
 * Local primitives for the weather + kiosk modules.
 *
 * Self-contained on purpose: the hub's and Settings' own ui files belong to other modules,
 * and reaching across that line couples this feature to files it doesn't own. Everything
 * here is token-only (src/styles.css), ≥44px touch targets, real labels, visible focus.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

type Variant = 'primary' | 'secondary';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunk',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${FOCUS} ${className}`}
    />
  );
}

const CONTROL =
  'min-h-11 w-full rounded-xl border border-border bg-surface px-3 text-base text-ink placeholder:text-ink-faint';

export function TextInput({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${FOCUS} ${className}`} />;
}

/** Status carries a word and a mark — never colour on its own. */
export function Note({
  tone,
  children,
  role,
}: {
  tone: 'warn' | 'info' | 'good';
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  const tones = {
    warn: 'border-clay/30 bg-clay-soft text-clay',
    info: 'border-border bg-surface-sunk text-ink-soft',
    good: 'border-sage/30 bg-sage-soft text-sage',
  } as const;
  const mark = { warn: '⚠', info: 'ⓘ', good: '✓' } as const;

  return (
    <p
      role={role}
      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm leading-relaxed ${tones[tone]}`}
    >
      <span aria-hidden="true">{mark[tone]}</span>
      <span>{children}</span>
    </p>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-surface-sunk ${className}`} />;
}

// ---------------------------------------------------------------- weather glyph

/** WMO code → a small stroke glyph, matching the app's icon style. Decorative only: the
 *  text label always carries the meaning, so weather is never signalled by shape/colour
 *  alone. `code < 0` renders a neutral "unknown" mark. */
export function WeatherGlyph({
  code,
  className = 'h-6 w-6',
}: {
  code: number;
  className?: string;
}) {
  const kind = glyphKind(code);
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
    focusable: false as const,
  };
  const cloud = <path d="M7 18a4 4 0 0 1-.5-7.97 5 5 0 0 1 9.66-1.2A3.5 3.5 0 0 1 16.5 18H7Z" />;

  switch (kind) {
    case 'sun':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
        </svg>
      );
    case 'partly':
      return (
        <svg {...common}>
          <path d="M8 6.5V5M4.8 8.3 3.7 7.2M12 9.2a3.2 3.2 0 0 0-4.6-2.9M4.5 12H3" />
          <circle cx="8" cy="9.2" r="2.6" />
          <path d="M9 19a3.5 3.5 0 0 1-.4-6.98 4.4 4.4 0 0 1 8.5-1 3 3 0 0 1 .4 5.98H9Z" />
        </svg>
      );
    case 'fog':
      return (
        <svg {...common}>
          {cloud}
          <path d="M5 21h10M8 18.5h9" />
        </svg>
      );
    case 'rain':
      return (
        <svg {...common}>
          {cloud}
          <path d="M8.5 20.5 8 22M12 20.5 11.5 22M15.5 20.5 15 22" />
        </svg>
      );
    case 'snow':
      return (
        <svg {...common}>
          {cloud}
          <path d="M8 21h.01M12 21.5h.01M16 21h.01" />
        </svg>
      );
    case 'storm':
      return (
        <svg {...common}>
          {cloud}
          <path d="M12 19l-2 3h3l-2 3" />
        </svg>
      );
    case 'cloud':
      return <svg {...common}>{cloud}</svg>;
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
          <path d="M9.5 10a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 1.8-2 3M12 17h.01" />
        </svg>
      );
  }
}

type GlyphKind = 'sun' | 'partly' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm' | 'unknown';

function glyphKind(code: number): GlyphKind {
  if (code === 0 || code === 1) return 'sun';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'fog';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code === 95 || code === 96 || code === 99) return 'storm';
  return 'unknown';
}

// ---------------------------------------------------------------- one-read hook

export interface Async<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  set: (value: T) => void;
}

/**
 * One read, with its own loading / error state. `load` is held in a ref so an inline arrow
 * doesn't re-run it forever. `deps` re-runs it (a kiosk refresh tick, a changed week).
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = []): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loadRef.current().then(
      (result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((value: T) => setData(value), []);

  return { data, error, loading, reload, set };
}
